<template>
    <div class="flex items-stretch relative" :class="{ 'col-span-2': item.width === 2 }">
        <div v-if="item.type === 'chart' && item.chart_options" class="bg-white relative pt-4 shadow-md rounded-lg px-2 w-full max-w-full">
            <EmptyChartState :options="item.chart_options" class="w-full grid">
                <GenericChart
                    destroy-on-update
                    :options="item.chart_options"
                    :has-options="item.burger_menu?.is_menu_applied === true"
                    :expandable="item.display_magnifier"
                    @ready="$emit('update:chart', $event)"
                    @update="$emit('update:chart', $event)"
                >
                    <template #prepend-actions>
                        <SwitchButton
                            v-if="item.toggle_option && item.toggle_option.is_toggle_applied"
                            :model-value="item.toggle_option.query_parameter_value"
                            :true-text="item.toggle_option.options[0].name"
                            :false-text="item.toggle_option.options[1].name"
                            :true-value="item.toggle_option.options[0].query_parameter_value"
                            :false-value="item.toggle_option.options[1].query_parameter_value"
                            :disabled="loading"
                            size="sm"
                            @update:model-value="onToggleOptionChange($event as string)"
                        />
                        <HelpWidgetButton v-if="item.help_widget_item" :item="item.help_widget_item" icon-size-class="size-5" />
                    </template>
                    <template #append-actions>
                        <div v-if="item.burger_menu?.is_menu_applied && item.burger_menu?.options?.length">
                            <ChartMultiSelect
                                v-model:value="selectedOptions"
                                :items="item.burger_menu.options"
                                :disabled="loading"
                                item-text="name"
                                item-value="query_parameter"
                                @closing="onOptionsMenuClosing"
                            />
                        </div>
                    </template>
                </GenericChart>
            </EmptyChartState>
        </div>
        <div v-else-if="item.type === 'table' && item.table" class="bg-white relative shadow-md rounded-lg w-full max-w-full overflow-hidden">
            <GenericTable :table-data="item.table" narrow>
                <template #actions>
                    <SwitchButton
                        v-if="item.toggle_option && item.toggle_option.is_toggle_applied"
                        :model-value="item.toggle_option.query_parameter_value"
                        :true-text="item.toggle_option.options[0].name"
                        :false-text="item.toggle_option.options[1].name"
                        :true-value="item.toggle_option.options[0].query_parameter_value"
                        :false-value="item.toggle_option.options[1].query_parameter_value"
                        :disabled="loading"
                        size="sm"
                        @update:model-value="onToggleOptionChange($event as string)"
                    />
                    <HelpWidgetButton v-if="item.help_widget_item" :item="item.help_widget_item" icon-size-class="size-5" class="flex-shrink-0" />
                </template>
            </GenericTable>
        </div>
        <div v-else-if="item.type === 'text' && item.text_content" class="w-full max-w-full space-y-1 mt-6 mb-2">
            <div class="font-bold text-lg leading-tight">{{ item.text_content.title }}</div>
            <div v-if="item.text_content.subtitle" class="text-gray-500 leading-tight">{{ item.text_content.subtitle }}</div>
            <div v-if="item.text_content.description" class="text-sm text-gray-500">{{ item.text_content.description }}</div>
        </div>
        <transition name="fade-fast" mode="out-in">
            <div v-if="loading" class="absolute inset-0 z-30 flex items-center justify-center bg-white/70">
                <Spinner class="h-8 w-8" />
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import * as Highcharts from 'highcharts';
import { ref, watch } from 'vue';
import { DashboardItem, DashboardBurgerMenuOption } from '@/types';
import ChartMultiSelect from '@/components/ui/ChartMultiSelect.vue';
import EmptyChartState from '@/components/charts/EmptyChartState.vue';
import GenericChart from '@/components/charts/GenericChart.vue';
import GenericTable from '@/components/common/GenericTable.vue';
import Spinner from '@/components/ui/Spinner.vue';
import HelpWidgetButton from '@/components/common/helpWidget/HelpWidgetButton.vue';
import SwitchButton from '@/components/ui/SwitchButton.vue';

interface Props {
    item: DashboardItem;
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    loading: false,
});
const emit = defineEmits<{
    'update:chart': [instance: Highcharts.Chart];
    'update:item': [value: DashboardItem];
    'update:options': [value: { options: DashboardBurgerMenuOption[]; hasChanged: boolean }];
    'update:toggle-option': [value: { query_parameter_key: string; query_parameter_value: string }];
}>();

const selectedOptions = ref(props.item.burger_menu?.options.filter((option) => option.is_applied) || []);

watch(
    () => props.item.burger_menu,
    (value) => {
        selectedOptions.value = value?.options.filter((option) => option.is_applied) || [];
    }
);

function onOptionsMenuClosing() {
    let hasChanged = (props.item.burger_menu?.options.filter((option) => option.is_applied) || []).length !== selectedOptions.value.length;

    for (const option of selectedOptions.value) {
        const propOption = props.item.burger_menu?.options.filter((o) => o.is_applied).find((o) => o.query_parameter === option.query_parameter);

        if (!propOption) {
            hasChanged = true;
        }
    }

    emit('update:options', {
        options: selectedOptions.value,
        hasChanged,
    });
}

function onToggleOptionChange(value: string) {
    emit('update:toggle-option', {
        query_parameter_key: props.item.toggle_option!.query_parameter_key,
        query_parameter_value: value,
    });
}
</script>
